import { Box } from "@mantine/core";
import { useAtom, useAtomValue, useSetAtom } from "jotai";
import { colors } from "theme";

import Button from "components/Button";
import Flex from "components/Flex";
import { CameraIcon } from "components/Icons/Camera";
import PageLayout from "components/PageLayout";
import PhotoGrid from "components/PhotoGrid";
import PhotoGridDocuments from "components/PhotoGridDocuments";
import PhotoUpload from "components/PhotoUpload";
import { Photo } from "components/PhotoUpload/types";
import Text from "components/Text";

import { additionalDocumentsAtom } from "atoms/additionalDocuments";
import { claimHandlingDecisionAtom } from "atoms/claimHandlingDecision";
import { damagePhotosAtom } from "atoms/damagePhotos";
import { pointsOfImpactPhAtom } from "atoms/pointsOfImpactDigitalJourney";
import { bodyShopAtom } from "atoms/selectBodyShop";
import { vandalismSeverityAtom } from "atoms/vandalismSeverity";
import { mileageAtom } from "atoms/vehicleCondition/mileage";
import { vehicleConditionQuestionsAtom } from "atoms/vehicleCondition/questions";
import { vehiclePhotosAtom } from "atoms/vehiclePhotos";
import { useCheckpoint } from "hooks/useCheckpoint";
import { useClientTranslation } from "hooks/useClientTranslation";
import { useDigitalJourneyPageRouting } from "hooks/useDigitalJourneyPageRouting";
import { useEventTracking } from "hooks/useEventTracking";
import { Checkpoint } from "types/Checkpoint";
import { VandalismSeverity } from "types/DigitalJourneySession";
import { processImageForUpload } from "utils/imageProcessing/processImageForUpload";

export const DJPhotosAdditionalDocumentsPage = () => {
  const { t } = useClientTranslation();
  const { trackEvent } = useEventTracking();
  const { onNext, continueCTA } = useDigitalJourneyPageRouting();
  const targetReached = useCheckpoint(Checkpoint.CLAIM_SUBMITTED);
  const [additionalDocuments, setAdditionalDocuments] = useAtom(additionalDocumentsAtom);
  const vandalismSeverity = useAtomValue(vandalismSeverityAtom);
  const setMileage = useSetAtom(mileageAtom);
  const setPointsOfImpactPh = useSetAtom(pointsOfImpactPhAtom);
  const setVehicleConditionQuestions = useSetAtom(vehicleConditionQuestionsAtom);
  const setSelectedBodyShop = useSetAtom(bodyShopAtom);
  const setVehiclePhotos = useSetAtom(vehiclePhotosAtom);
  const setDamagePhotos = useSetAtom(damagePhotosAtom);
  const setClaimHandlingDecision = useSetAtom(claimHandlingDecisionAtom);

  const canContinue = true;

  const onContinue = () => {
    trackEvent({
      name: "Digital Journey Additional Documents Photos - continue",
      properties: { numberOfImages: additionalDocuments.length },
    });
    if (
      vandalismSeverity &&
      (vandalismSeverity === VandalismSeverity.TOTAL_FIRE || vandalismSeverity === VandalismSeverity.TOTAL_THEFT)
    ) {
      setMileage("");
      setPointsOfImpactPh([]);
      setVehicleConditionQuestions({
        isVehicleDrivable: undefined,
        haveAirbagsDeployed: undefined,
        windshieldShattered: undefined,
        canOpenAndCloseAllDoors: undefined,
      });
      setSelectedBodyShop(null);
      setDamagePhotos([]);
      setVehiclePhotos([]);
      setClaimHandlingDecision(null);
    }
    onNext();
  };

  const onDrop = (files: File[]) => {
    files.forEach(async (file, index) => {
      const image = await processImageForUpload({
        file,
        alt: `Additional Document photo to upload number ${index + 1}`,
        type: Photo.Additional,
      });

      setAdditionalDocuments((photos) => [...photos, image]);
    });
  };

  return (
    <PageLayout
      contentSpacing="space-between"
      dataTestId="additionalDocuments"
      forceContinue={targetReached}
      bottomButtons={
        <Flex gap={16}>
          <PhotoUpload onDrop={onDrop} disableGalleryUploadOverride={false}>
            <Button typeVariant={canContinue ? "secondary" : "primary"} leftIcon={<CameraIcon />}>
              {t("global.add_photos")}
            </Button>
          </PhotoUpload>
          {canContinue && <Button onClick={onContinue}>{continueCTA}</Button>}
        </Flex>
      }
    >
      <Flex mx={24} mb={32} my={8} gap={16}>
        <Flex gap={8}>
          <Text as="h1" sizeVariant="xl" weight={600}>
            Vuoi caricare ulteriori documenti?
          </Text>
          <Box>
            <Text>
              Es., denuncia scritta del sinistro firmata da te, dichiarazione testimoniale, grafico dell'incidente o
              altro.
            </Text>
            <Text>
              <Text as="span" weight={700}>
                N.B.{" "}
              </Text>
              Eventuali lettere di incarico professionale, lettere di messa in mora e/o richieste da parte di terzi
              incaricati relative a questa pratica di sinistro dovranno essere inviate secondo le modalità previste
              all'interno del{" "}
              <a
                href={t("digital_journey.welcome.first_disclaimer_link_path")}
                style={{ color: colors.primary }}
                target="_blank"
                rel="noreferrer"
              >
                {t("digital_journey.welcome.first_disclaimer_link_text")}
              </a>
              .
            </Text>
            <Text>(Non obbligatorio)</Text>
          </Box>
        </Flex>
        {Boolean(additionalDocuments.length) ? (
          <PhotoGrid images={additionalDocuments} deleteFromAtom={setAdditionalDocuments} />
        ) : (
          <Flex
            align="center"
            sx={{
              "& > div": {
                width: "unset",
              },
            }}
          >
            <PhotoGridDocuments
              sxImageContainer={{
                "&&": {
                  maxHeight: "354px",
                },
              }}
              images={[
                {
                  id: "djAdditionalDocumentsPhoto",
                  alt: "Additional Document Photos",
                  src: "/assets/png/digitalJourney/additional-documents-example.png",
                },
              ]}
            />
          </Flex>
        )}
      </Flex>
    </PageLayout>
  );
};
